import GuildContext, { useGuild } from 'app/frontend/base/config/context/guild'
import EntityMenu from 'app/frontend/base/components/EntityMenu'
import EntityMenuMobileList from 'app/frontend/base/components/EntityMenuMobileList'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Menu } from 'lucide-react'
import { useTheme } from '@/components/theme-provider'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import HeaderDropdown from './HeaderDropdown'
import LoginButtons from 'app/frontend/base/components/LoginButtons'
import { useGuildMenuItems } from '../../GuildDashboard/components/GuildHeader'
import {
  CustomDomainSelectedTeamMenu,
  CustomDomainTeamMenu,
  CustomDomainTeamSwitcher,
  DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT,
  getCustomDomainTeamsWithAccess,
  getSelectedCustomDomainTeam,
} from './CustomDomainTeamNavigation'
import { Label } from '@/components/ui/label'
import Card from 'app/frontend/base/components/Card'
import { useTranslation } from 'react-i18next'

const CustomDomainHeader = () => {
  const { currentScheme } = useTheme()
  const { customDomain } = useCustomDomain()

  if (!customDomain) return null

  const headerLogoPath =
    currentScheme === 'dark'
      ? customDomain?.darkModeHeaderLogoPath
      : customDomain?.lightModeHeaderLogoPath

  return (
    <div className='flex min-w-0 flex-1 items-center gap-4'>
      <Link to='/' className='flex items-center gap-2 shrink-0'>
        {headerLogoPath ? (
          <img src={headerLogoPath} className='h-10 min-h-10 w-auto' />
        ) : (
          <BannerImage
            className='w-12 h-12 min-w-12 min-h-12'
            faction={customDomain.guild.faction}
            fileName={customDomain.guild.bannerFilename}
            size='custom'
          />
        )}

        {customDomain.headerShowName ? (
          <span className='text-xl font-bold text-accent-foreground max-sm:hidden'>
            {customDomain.guild.name}
          </span>
        ) : null}
      </Link>

      <div className='flex min-w-0 flex-1 items-center gap-0'>
        {customDomain.team ? (
          <GuildContext.Provider
            value={{
              guild: customDomain.guild,
              guildReference: customDomain.guild,
              currentTeam: customDomain.team,
              teamReference: customDomain.team,
            }}
          >
            <CustomDomainTeamHeader />
          </GuildContext.Provider>
        ) : (
          <GuildContext.Provider
            value={{
              guild: customDomain.guild,
              guildReference: customDomain.guild,
            }}
          >
            <CustomDomainGuildHeader />
          </GuildContext.Provider>
        )}
      </div>
    </div>
  )
}

const CustomDomainGuildHeader = () => {
  const { customDomain } = useCustomDomain()
  const mainMenuContent =
    customDomain?.headerConfig?.mainMenuContent ??
    DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT
  const selectedTeam = getSelectedCustomDomainTeam(customDomain)
  const showTeamSwitcher =
    mainMenuContent !== 'only_guild_menu' &&
    getCustomDomainTeamsWithAccess(customDomain).length > 0

  return (
    <GuildContext.Provider
      value={{
        guild: customDomain?.guild,
        guildReference: customDomain?.guild,
        teamReference: selectedTeam,
      }}
    >
      <div className='flex min-w-0 flex-1 items-center gap-4'>
        {showTeamSwitcher ? (
          <div className='min-w-0 max-sm:flex-1 sm:shrink-0'>
            <CustomDomainTeamSwitcher
              selectedTeam={selectedTeam}
              showSelectedTeamLabel={true}
            />
          </div>
        ) : null}

        <div className='min-w-0 flex-1 max-sm:hidden'>
          <CustomDomainGuildMenu mainMenuContent={mainMenuContent} />
        </div>

        <div className='ml-auto sm:hidden'>
          <CustomDomainGuildMobileMenu mainMenuContent={mainMenuContent} />
        </div>
      </div>
    </GuildContext.Provider>
  )
}

const CustomDomainMobileMenu = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        aria-label='Menu'
        className='inline-flex items-center justify-center rounded-md p-2 hover:bg-accent transition-colors'
      >
        <Menu className='h-6 w-6' />
      </SheetTrigger>
      <SheetContent
        side='right'
        aria-describedby={undefined}
        className='flex w-[85vw] flex-col gap-4 overflow-y-auto p-4 pt-12'
      >
        <SheetTitle className='sr-only'>Menu</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  )
}

const CustomDomainGuildMobileMenu = ({
  mainMenuContent,
}: {
  mainMenuContent: wowaudit.CustomDomain['headerConfig']['mainMenuContent']
}) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()

  return (
    <CustomDomainMobileMenu>
      {user ? (
        <HeaderDropdown variant='bar' />
      ) : (
        <Card>
          <Label className='text-sm font-medium mb-2 block text-center'>
            {t('header.log-in-with')}
          </Label>
          <LoginButtons grow={true} />
        </Card>
      )}

      <CustomDomainGuildMenu
        mainMenuContent={mainMenuContent}
        variant='mobile'
      />
    </CustomDomainMobileMenu>
  )
}

const CustomDomainTeamHeader = () => {
  const {
    data: { user },
  } = usePageInfo()

  return (
    <>
      <div className='min-w-0 flex-1 max-sm:hidden'>
        <CustomDomainTeamMenu />
      </div>

      <div className='ml-auto flex items-center gap-1 sm:hidden'>
        {user ? <HeaderDropdown /> : <LoginButtons />}
        <CustomDomainMobileMenu>
          <CustomDomainTeamMenu variant='mobile' />
        </CustomDomainMobileMenu>
      </div>
    </>
  )
}

const CustomDomainGuildMenu = ({
  mainMenuContent,
  variant = 'header',
}: {
  mainMenuContent: wowaudit.CustomDomain['headerConfig']['mainMenuContent']
  variant?: 'header' | 'mobile'
}) => {
  const menuItems = useGuildMenuItems()
  const { guild, guildReference, teamReference } = useGuild()
  const showTeamMenu =
    mainMenuContent === 'team_switcher_and_team_menu' && teamReference

  if (showTeamMenu) {
    return (
      <CustomDomainSelectedTeamMenu
        selectedTeam={teamReference}
        guild={guild}
        guildReference={guildReference}
        variant={variant}
      />
    )
  }

  if (!menuItems.length) return null

  return (
    <GuildContext.Provider value={{ guild, guildReference }}>
      {variant === 'mobile' ? (
        <EntityMenuMobileList menuItems={menuItems} />
      ) : (
        <EntityMenu menuItems={menuItems} placement='header' />
      )}
    </GuildContext.Provider>
  )
}

export default CustomDomainHeader
